import { useMemo, useState } from 'react';
import { LargePanePage, PageTitle } from 'components';

import { AnalyticsCategoryProvider, Categories } from '@app/analytics';
import { useCheckOrganizationInvitation } from '../../../queries';
import {
  TeamMemberSignupError,
  TeamMemberSignupErrorReason,
} from './TeamMemberSignupError';
import { Trans, useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { ApiFormattedException } from 'store/modules/exceptions';
import { SignupForm } from '../SignupForm';
import { JoinOrganizationPage } from './JoinOrganizationPage';

export const TeamMemberSignup = () => {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const {
    data: invitation,
    error,
    isLoading,
  } = useCheckOrganizationInvitation(id);

  const [errorReason, setErrorReason] = useState<
    TeamMemberSignupErrorReason | undefined
  >(undefined);

  const typedError = error as ApiFormattedException | undefined;
  const errorCode =
    typedError && 'getCode' in typedError ? typedError.getCode() : '';
  const errorMessage =
    typedError && 'getMessage' in typedError ? typedError.getMessage() : '';

  const pageContent = useMemo(() => {
    if (errorMessage === 'Token expired') {
      setErrorReason('invitationExpired');
    } else if (errorMessage === 'Email already used') {
      setErrorReason('emailAlreadyUsed');
    } else if (errorMessage === 'Token already used') {
      setErrorReason('invitationAlreadyUsed');
    } else if (errorCode === 'NOT_FOUND') {
      setErrorReason('invitationNotFound');
    } else if (error) {
      setErrorReason('unkownError');
    }
    if (isLoading) {
      return null;
    }
    if (errorReason) {
      return <TeamMemberSignupError errorReason={errorReason} />;
    }

    if (invitation?.account?.id && invitation?.account?.isAlreadyClient && id) {
      return <JoinOrganizationPage invitation={invitation} token={id} />;
    }
    if (invitation) {
      return (
        <SignupForm
          organisationName={invitation?.organizationName}
          email={invitation?.email}
          invitationToken={id}
          accountData={invitation?.account}
        />
      );
    }
    return <TeamMemberSignupError errorReason={'unkownError'} />;
  }, [errorMessage, errorCode, error, isLoading, errorReason, invitation, id]);

  return (
    <AnalyticsCategoryProvider category={Categories.SIGNUP}>
      <LargePanePage
        color="red"
        panelContent={
          <h1>
            <Trans i18nKey="signup.client.largeTitle" />
          </h1>
        }
      >
        <PageTitle title={t('signup.metaTitle')} />
        {pageContent}
      </LargePanePage>
    </AnalyticsCategoryProvider>
  );
};
